/**
 * Payments module DTOs (§10, §15.2): initialize / status / webhook, admin refunds, payouts, reconciliation.
 * Money is strings in transit (D01). Provider references are unique; webhooks are unreliable until verified.
 */
import { z } from 'zod';
import { IsoDateTime, Ulid, paginated } from './common.js';
import { LedgerAccount, PaymentStatus } from './enums.js';
import { MoneyDto, SignedMoneyDto } from './money.js';
export const PaymentProviderName = z.string().regex(/^[a-z][a-z0-9_-]{1,31}$/);
export const InitializePaymentRequest = z.object({
    entryId: Ulid,
    returnUrl: z.string().max(512).optional(),
    /** Provider-specific method hint (card, bank, wallet); never card data. */
    methodHint: z.string().max(32).optional(),
});
export const CheckoutDto = z.object({
    paymentId: Ulid,
    status: PaymentStatus,
    amount: MoneyDto,
    provider: PaymentProviderName,
    /** Hosted / provider-controlled checkout keeps card data out of scope (§10.1). */
    checkoutUrl: z.string().optional(),
    expiresAt: IsoDateTime.optional(),
});
export const PaymentStatusDto = z.object({
    paymentId: Ulid,
    entryId: Ulid,
    status: PaymentStatus,
    amount: MoneyDto,
    refundedAmount: MoneyDto.optional(),
    provider: PaymentProviderName,
    /** §10.2 participant display text. */
    displayText: z.string(),
    nextAction: z.enum(['NONE', 'CHECK_STATUS', 'RETRY', 'RESTART', 'CONTACT_SUPPORT']),
    updatedAt: IsoDateTime,
    /** When the UI may poll again (pending). */
    pollAfter: IsoDateTime.optional(),
    supportCaseRef: z.string().optional(),
});
/** Normalized provider event after signature, timestamp-tolerance and replay checks (§10.3 #2). */
export const NormalizedPaymentEvent = z.object({
    provider: PaymentProviderName,
    providerEventId: z.string().min(1).max(128),
    providerReference: z.string().min(1).max(128),
    kind: z.enum(['SUCCEEDED', 'FAILED', 'PENDING', 'EXPIRED', 'CANCELLED', 'REFUNDED', 'CHARGEBACK']),
    amount: MoneyDto,
    occurredAt: IsoDateTime,
    receivedAt: IsoDateTime,
    raw: z.record(z.string(), z.unknown()).optional(),
});
export const WebhookAckDto = z.object({
    accepted: z.boolean(),
    duplicate: z.boolean(),
    paymentId: Ulid.optional(),
    resultingStatus: PaymentStatus.optional(),
});
// ---------------------------------------------------------------------------------------------------------
// Admin finance
// ---------------------------------------------------------------------------------------------------------
export const RefundReason = z.enum([
    'LATE_PAYMENT',
    'LOTTERY_CANCELLED',
    'PARTICIPANT_REQUEST',
    'INELIGIBLE',
    'ENTRY_FAILED_AFTER_PAYMENT',
    'DUPLICATE',
    'GOODWILL',
    'CHARGEBACK',
]);
export const CreateRefundRequest = z.object({
    paymentId: Ulid,
    /** Omit for a full refund. */
    amount: MoneyDto.optional(),
    reason: RefundReason,
    note: z.string().max(2000).optional(),
    evidenceRef: z.string().max(512).optional(),
});
export const RefundDto = z.object({
    refundId: Ulid,
    paymentId: Ulid,
    amount: MoneyDto,
    reason: RefundReason,
    status: z.enum(['REQUESTED', 'PROCESSING', 'COMPLETED', 'FAILED']),
    providerReference: z.string().nullable(),
    requestedBy: Ulid,
    createdAt: IsoDateTime,
    completedAt: IsoDateTime.nullable(),
    attempts: z.number().int(),
    nextAction: z.string().optional(),
});
export const PayoutDto = z.object({
    payoutId: Ulid,
    claimId: Ulid,
    winId: z.string(),
    amount: MoneyDto,
    status: z.enum(['PENDING_APPROVAL', 'APPROVED', 'INITIATED', 'COMPLETED', 'REJECTED', 'FAILED']),
    /** Masked destination (§10.4 payout rejected: mask destination data). */
    destinationMasked: z.string(),
    requiresDualApproval: z.boolean(),
    approvals: z.array(z.object({ staffId: Ulid, at: IsoDateTime })),
    evidenceRefs: z.array(z.string()),
    providerReference: z.string().nullable(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
    attempts: z.number().int(),
    age: z.number().int().describe('minutes since creation'),
});
export const ApprovePayoutRequest = z.object({
    /** Evidence must be attached before completion (§10.5). */
    evidenceRef: z.string().max(512).optional(),
    note: z.string().max(2000).optional(),
});
export const LedgerLineDto = z.object({
    lineId: Ulid,
    journalId: Ulid,
    account: LedgerAccount,
    /** Positive = debit, negative = credit; every journal sums to zero (§10.3 ledger rule). */
    amount: SignedMoneyDto,
    paymentId: Ulid.nullable(),
    refundId: Ulid.nullable(),
    payoutId: Ulid.nullable(),
    memo: z.string().max(200),
    postedAt: IsoDateTime,
    reversesLineId: Ulid.nullable(),
});
export const ReconciliationQuery = z.object({
    lotteryId: Ulid.optional(),
    from: IsoDateTime.optional(),
    to: IsoDateTime.optional(),
    onlyExceptions: z.coerce.boolean().default(false),
});
export const ReconciliationExceptionDto = z.object({
    exceptionId: Ulid,
    paymentId: Ulid.nullable(),
    providerReference: z.string().nullable(),
    kind: z.enum([
        'MISSING_LOCAL',
        'MISSING_PROVIDER',
        'AMOUNT_MISMATCH',
        'STATUS_MISMATCH',
        'LEDGER_IMBALANCE',
        'ENTRY_MISMATCH',
    ]),
    localAmount: MoneyDto.nullable(),
    providerAmount: MoneyDto.nullable(),
    detectedAt: IsoDateTime,
    owner: Ulid.nullable(),
    resolvedAt: IsoDateTime.nullable(),
    participantSafeExplanation: z.string(),
    blocksDraw: z.boolean(),
});
export const ReconciliationReportDto = z.object({
    generatedAt: IsoDateTime,
    window: z.object({ from: IsoDateTime, to: IsoDateTime }),
    intents: z.number().int(),
    providerTransactions: z.number().int(),
    succeeded: z.number().int(),
    refunds: z.number().int(),
    payouts: z.number().int(),
    grossVolume: MoneyDto,
    providerFees: MoneyDto,
    refundedVolume: MoneyDto,
    chargebacks: MoneyDto,
    prizeCost: MoneyDto,
    netRevenue: SignedMoneyDto,
    ledgerBalanced: z.boolean(),
    exceptions: paginated(ReconciliationExceptionDto),
});
// ---------------------------------------------------------------------------------------------------------
// Payments agent extensions (D-1501 … D-1506): finance console DTOs, reconciliation rows, pure ledger rules.
// Appended per docs/MODULE_AGENT_BRIEF.md — nothing above this line changed.
// ---------------------------------------------------------------------------------------------------------
/** Runbook §2 exception kinds (docs/runbooks/payment-reconciliation.md) plus the §10.4 after-win cases. */
export const ReconciliationExceptionKind = z.enum([
    'missing_intent',
    'orphan_provider_tx',
    'amount_mismatch',
    'ledger_drift',
    'refund_mismatch',
    'payout_mismatch',
    'entry_mismatch',
    'chargeback',
    'refund_after_win',
]);
export const ReconciliationExceptionStatus = z.enum(['OPEN', 'INVESTIGATING', 'RESOLVED', 'WRITTEN_OFF']);
/** Participant-safe explanation per exception kind (§10.5 "every exception has a participant-safe explanation"). */
export const RECONCILIATION_EXCEPTION_COPY = {
    missing_intent: 'We are checking a payment with our payment service. No entry is affected until it is confirmed.',
    orphan_provider_tx: 'We are confirming your payment with our payment service. Your entry is on hold until it is confirmed.',
    amount_mismatch: 'The amount our payment service reported differs from the amount shown to you. We are resolving this now.',
    ledger_drift: 'Our finance team is reviewing a bookkeeping difference. No entry is affected.',
    refund_mismatch: 'We are confirming a refund with our payment service. You will see the final status here.',
    payout_mismatch: 'We are confirming a prize transfer with our bank. Your prize is still yours.',
    entry_mismatch: 'We received your payment but could not confirm your entry. We are resolving this now.',
    chargeback: 'Your payment service has questioned this payment. Your entry or prize is on hold while we review it.',
    refund_after_win: 'A refund was recorded on the payment linked to your win. Your prize is on hold while we review it.',
};
export const ReconciliationExceptionRowDto = z.object({
    exceptionId: Ulid,
    runId: Ulid,
    lotteryId: Ulid.nullable(),
    paymentId: Ulid.nullable(),
    entryId: Ulid.nullable(),
    providerReference: z.string().nullable(),
    kind: ReconciliationExceptionKind,
    status: ReconciliationExceptionStatus,
    expected: MoneyDto.nullable(),
    actual: MoneyDto.nullable(),
    detail: z.record(z.string(), z.unknown()),
    blocksDraw: z.boolean(),
    owner: Ulid.nullable(),
    attempts: z.number().int(),
    nextAction: z.string(),
    participantSafeExplanation: z.string(),
    supportCaseId: z.string().nullable(),
    resolvedBy: Ulid.nullable(),
    resolution: z.string().nullable(),
    resolvedAt: IsoDateTime.nullable(),
    createdAt: IsoDateTime,
    /** Minutes since creation (queue age). */
    ageMinutes: z.number().int(),
});
export const ReconciliationRunDto = z.object({
    runId: Ulid,
    lotteryId: Ulid.nullable(),
    trigger: z.enum(['daily', 'on_draw', 'manual']),
    provider: z.string(),
    window: z.object({ from: IsoDateTime, to: IsoDateTime }),
    summary: z.record(z.string(), z.unknown()),
    exceptionCount: z.number().int(),
    blocksDraw: z.boolean(),
    startedBy: z.string().nullable(),
    startedAt: IsoDateTime,
    completedAt: IsoDateTime.nullable(),
});
export const ReconciliationExceptionListQuery = z.object({
    lotteryId: Ulid.optional(),
    status: ReconciliationExceptionStatus.optional(),
    kind: ReconciliationExceptionKind.optional(),
    blocksDraw: z.coerce.boolean().optional(),
});
export const ResolveReconciliationExceptionRequest = z.object({
    status: z.enum(['INVESTIGATING', 'RESOLVED', 'WRITTEN_OFF']),
    resolution: z.string().min(10).max(2000),
    owner: Ulid.optional(),
    supportCaseId: z.string().max(64).optional(),
});
/** `GET /v1/admin/reconciliation` (§16.2 Finance → provider reconciliation). */
export const ReconciliationOverviewDto = z.object({
    generatedAt: IsoDateTime,
    lotteryId: Ulid.nullable(),
    openExceptions: z.number().int(),
    blockingExceptions: z.number().int(),
    ledgerBalanced: z.boolean(),
    lastRun: ReconciliationRunDto.nullable(),
    runs: z.array(ReconciliationRunDto),
    exceptions: z.array(ReconciliationExceptionRowDto),
});
export const RunReconciliationRequest = z.object({
    lotteryId: Ulid.optional(),
    trigger: z.enum(['on_draw', 'manual']).default('manual'),
});
export const PaymentListQuery = z.object({
    lotteryId: Ulid.optional(),
    status: PaymentStatus.optional(),
    participantId: Ulid.optional(),
    providerReference: z.string().max(128).optional(),
});
/** Finance list row: masked participant, never card or contact data. */
export const PaymentAdminRowDto = z.object({
    paymentId: Ulid,
    lotteryId: Ulid,
    participantId: Ulid,
    provider: PaymentProviderName,
    providerReference: z.string().nullable(),
    status: PaymentStatus,
    amount: MoneyDto,
    fee: MoneyDto.nullable(),
    refundedAmount: MoneyDto,
    quantity: z.number().int(),
    entryIds: z.array(Ulid),
    lateAfterClose: z.boolean(),
    failureCode: z.string().nullable(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
    succeededAt: IsoDateTime.nullable(),
    displayText: z.string(),
});
export const RefundListQuery = z.object({
    lotteryId: Ulid.optional(),
    paymentId: Ulid.optional(),
    status: z.enum(['REQUESTED', 'APPROVED', 'PROCESSING', 'SUCCEEDED', 'FAILED', 'CANCELLED']).optional(),
});
/** Refund queue row (§10.5: age, amount, owner, attempts, next action + participant-safe explanation). */
export const RefundRowDto = RefundDto.extend({
    lotteryId: Ulid,
    reasonCode: z.string(),
    note: z.string().nullable(),
    storedStatus: z.enum(['REQUESTED', 'APPROVED', 'PROCESSING', 'SUCCEEDED', 'FAILED', 'CANCELLED']),
    failureCode: z.string().nullable(),
    entryEffect: z.string(),
    participantSafeExplanation: z.string(),
    ageMinutes: z.number().int(),
    owner: Ulid.nullable(),
});
/** `POST /v1/admin/lotteries/:id/refund-wave` (§10.4 lottery cancelled): dry run first, then execute. */
export const RefundWaveRequest = z.object({
    dryRun: z.boolean().default(true),
    note: z.string().max(2000).optional(),
});
export const RefundWaveDto = z.object({
    lotteryId: Ulid,
    dryRun: z.boolean(),
    /** Confirmed paid payments still holding a refundable balance. */
    eligible: z.number().int(),
    eligibleAmount: MoneyDto,
    /** Already refunded before this wave (progress). */
    alreadyRefunded: z.number().int(),
    requested: z.number().int(),
    succeeded: z.number().int(),
    failed: z.number().int(),
    refundedAmount: MoneyDto,
    /** 0–100 aggregate refund progress published to participants (§10.4). */
    progressPercent: z.number().int().min(0).max(100),
});
export const PayoutListQuery = z.object({
    lotteryId: Ulid.optional(),
    status: z
        .enum(['PENDING_APPROVAL', 'APPROVED', 'INITIATED', 'SUCCEEDED', 'FAILED', 'REJECTED', 'CANCELLED'])
        .optional(),
    winId: z.string().max(32).optional(),
});
/** `POST /v1/admin/payouts` (D04): a cash payout request for an approved claim (maker). */
export const CreatePayoutRequest = z.object({
    claimId: Ulid,
    /** claims.PayoutDestination id; defaults to the claim's current (non-invalidated) destination. */
    destinationId: Ulid.optional(),
    /** Defaults to the claim's selected value / prize value. */
    amount: MoneyDto.optional(),
    evidenceRef: z.string().max(512).optional(),
    note: z.string().max(2000).optional(),
});
export const RejectPayoutRequest = z.object({ reason: z.string().min(3).max(2000) });
/** Manual transfer record when the provider has no payout capability (D04 launch mode). */
export const CompletePayoutRequest = z.object({
    bankReference: z.string().min(3).max(128),
    evidenceRef: z.string().max(512).optional(),
    note: z.string().max(2000).optional(),
});
/** §10.4 payout rejected: retry as a new linked attempt, optionally to a corrected destination. */
export const RetryPayoutRequest = z.object({
    destinationId: Ulid.optional(),
    note: z.string().max(2000).optional(),
});
export const PayoutDetailDto = PayoutDto.extend({
    lotteryId: Ulid,
    storedStatus: z.enum([
        'PENDING_APPROVAL',
        'APPROVED',
        'INITIATED',
        'SUCCEEDED',
        'FAILED',
        'REJECTED',
        'CANCELLED',
    ]),
    requestedBy: Ulid,
    approvalsNeeded: z.number().int(),
    rejectionReason: z.string().nullable(),
    failureCode: z.string().nullable(),
    previousPayoutId: Ulid.nullable(),
    bankReference: z.string().nullable(),
    /** Whether the configured provider executes payouts; otherwise finance records the manual transfer. */
    providerPayouts: z.boolean(),
    nextAction: z.string(),
    participantSafeExplanation: z.string(),
    owner: Ulid.nullable(),
});
export const LedgerQuery = z.object({
    lotteryId: Ulid.optional(),
    paymentId: Ulid.optional(),
    reference: z.string().max(64).optional(),
    account: LedgerAccount.optional(),
    from: IsoDateTime.optional(),
    to: IsoDateTime.optional(),
    format: z.enum(['json', 'csv']).default('json'),
});
export const LedgerBalanceDto = z.object({
    account: LedgerAccount,
    currency: z.string(),
    debits: MoneyDto,
    credits: MoneyDto,
    /** debits − credits (signed). */
    balance: SignedMoneyDto,
});
export const LedgerTransactionDto = z.object({
    journalId: Ulid,
    lotteryId: Ulid.nullable(),
    kind: z.string(),
    currency: z.string(),
    reference: z.string(),
    description: z.string().nullable(),
    reversesJournalId: Ulid.nullable(),
    postedBy: z.string().nullable(),
    postedAt: IsoDateTime,
    lines: z.array(LedgerLineDto),
});
export const LedgerPageDto = z.object({
    generatedAt: IsoDateTime,
    balances: z.array(LedgerBalanceDto),
    /** Σdebits − Σcredits per currency; must be "0" (§10.3 ledger rule). */
    imbalance: z.array(SignedMoneyDto),
    balanced: z.boolean(),
    transactions: paginated(LedgerTransactionDto),
});
/** `GET /v1/admin/lotteries/:id/finance` (§10.5, §19 revenue derived from the ledger). */
export const LotteryFinanceDto = z.object({
    lotteryId: Ulid,
    currency: z.string(),
    generatedAt: IsoDateTime,
    payments: z.object({
        initialized: z.number().int(),
        pending: z.number().int(),
        succeeded: z.number().int(),
        failed: z.number().int(),
        refunded: z.number().int(),
        exceptions: z.number().int(),
    }),
    grossVolume: MoneyDto,
    providerFees: MoneyDto,
    refunds: MoneyDto,
    chargebacks: MoneyDto,
    taxes: MoneyDto,
    prizeCost: MoneyDto,
    payoutsPayable: MoneyDto,
    /** gross − fees − refunds − chargebacks − taxes − prize cost. Not gross payment volume (§10.5). */
    netRevenue: SignedMoneyDto,
    ledgerBalanced: z.boolean(),
    openExceptions: z.number().int(),
    blockingExceptions: z.number().int(),
});
const line = (account, debitMinor, creditMinor, memo) => debitMinor === 0n && creditMinor === 0n ? [] : [{ account, debitMinor, creditMinor, memo }];
export const isBalancedLines = (lines) => lines.reduce((acc, l) => acc + l.debitMinor - l.creditMinor, 0n) === 0n &&
    lines.every((l) => (l.debitMinor === 0n) !== (l.creditMinor === 0n) && l.debitMinor >= 0n && l.creditMinor >= 0n);
/** Payment succeeded: receivable recognised as revenue, then settled by the provider net of its fee. */
export const paymentSuccessLedgerLines = (grossMinor, feeMinor = 0n) => {
    if (grossMinor <= 0n)
        throw new RangeError('payment amount must be positive');
    if (feeMinor < 0n || feeMinor > grossMinor)
        throw new RangeError('provider fee out of range');
    return [
        ...line('PARTICIPANT_RECEIVABLE', grossMinor, 0n, 'entry purchase'),
        ...line('ENTRY_REVENUE', 0n, grossMinor, 'entry revenue'),
        ...line('PROVIDER_CLEARING', grossMinor - feeMinor, 0n, 'provider settlement'),
        ...line('PROVIDER_FEES', feeMinor, 0n, 'provider fee'),
        ...line('PARTICIPANT_RECEIVABLE', 0n, grossMinor, 'settled by provider'),
    ];
};
/** Refund (full or partial): a reversing journal, never an edit (§10.3). */
export const refundLedgerLines = (amountMinor) => {
    if (amountMinor <= 0n)
        throw new RangeError('refund amount must be positive');
    return [
        ...line('REFUNDS', amountMinor, 0n, 'refund'),
        ...line('PROVIDER_CLEARING', 0n, amountMinor, 'refund paid'),
    ];
};
/** Chargeback opened: money leaves clearing into the chargebacks category (hold, review, never delete). */
export const chargebackLedgerLines = (amountMinor) => {
    if (amountMinor <= 0n)
        throw new RangeError('chargeback amount must be positive');
    return [
        ...line('CHARGEBACKS', amountMinor, 0n, 'chargeback'),
        ...line('PROVIDER_CLEARING', 0n, amountMinor, 'chargeback withheld'),
    ];
};
/** Payout approved: prize cost recognised, liability to the winner recorded. */
export const payoutApprovedLedgerLines = (amountMinor) => {
    if (amountMinor <= 0n)
        throw new RangeError('payout amount must be positive');
    return [
        ...line('PRIZE_COST', amountMinor, 0n, 'cash prize'),
        ...line('PAYOUTS_PAYABLE', 0n, amountMinor, 'payable to winner'),
    ];
};
/** Payout settled (provider or manual transfer): liability cleared from the settlement account. */
export const payoutSettledLedgerLines = (amountMinor) => {
    if (amountMinor <= 0n)
        throw new RangeError('payout amount must be positive');
    return [
        ...line('PAYOUTS_PAYABLE', amountMinor, 0n, 'payout settled'),
        ...line('SETTLEMENT', 0n, amountMinor, 'transfer out'),
    ];
};
/** §19 "Revenue": net = gross − fees − refunds − chargebacks − taxes − prize cost, from ledger balances. */
export const netRevenueMinor = (b) => b.revenue - b.fees - b.refunds - b.chargebacks - b.taxes - b.prizeCost;
/** Participant-safe next action per payment state (§10.2 table). */
export const PAYMENT_NEXT_ACTION = {
    INITIALIZED: 'CHECK_STATUS',
    PENDING: 'CHECK_STATUS',
    SUCCEEDED: 'NONE',
    FAILED: 'RETRY',
    EXPIRED: 'RESTART',
    CANCELLED: 'RESTART',
    REFUNDED: 'NONE',
    PARTIALLY_REFUNDED: 'NONE',
    RECONCILIATION_EXCEPTION: 'CONTACT_SUPPORT',
};
//# sourceMappingURL=payments.js.map